import { inject, Injectable } from '@angular/core';
import { NonNullableFormBuilder, Validators } from '@angular/forms';

import {
  type DefineFormState,
  provideDynamicFormConfigs,
} from '@core/ask-hr-dynamic-forms-smart-ui';
import {
  BooleanConfig,
  DateConfig,
  DropdownConfig,
  FileConfig,
  InfoboxConfig,
  LinkConfig,
  NumberConfig,
  SelectMultiConfig,
  SelectSingleConfig,
  TextMultiLineConfig,
  TextSingleLineConfig,
} from '@core/ask-hr-smart-ui';
import type {
  AnyTemplateItem,
  AskPredefinedTemplate,
  AskTemplate,
} from '@core/ask-hr-templates-types';
import { ProsemirrorBasicConfig } from '@core/prosemirror-legacy/webapp';
import type { SymbolEmoji } from '@core/symbol-legacy';
import { generateUuid } from '@core/utils-legacy';

export const FormKindProviders = <const>[
  provideDynamicFormConfigs({ useValue: TextSingleLineConfig }),
  provideDynamicFormConfigs({ useValue: LinkConfig }),
  provideDynamicFormConfigs({ useValue: TextMultiLineConfig }),
  provideDynamicFormConfigs({ useValue: BooleanConfig }),
  provideDynamicFormConfigs({ useValue: SelectSingleConfig }),
  provideDynamicFormConfigs({ useValue: SelectMultiConfig }),
  provideDynamicFormConfigs({ useValue: DropdownConfig }),
  provideDynamicFormConfigs({ useValue: DateConfig }),
  provideDynamicFormConfigs({ useValue: NumberConfig }),
  provideDynamicFormConfigs({ useValue: FileConfig }),
  provideDynamicFormConfigs({ useValue: InfoboxConfig }),
];

export type AskHrDynamicFormConfig = InstanceType<(typeof FormKindProviders)[number]['useValue']>;
export type AskTemplateFormState = DefineFormState<AskHrDynamicFormConfig>;

export type DefineQuestionKind =
  | 'string'
  | 'text'
  | 'boolean'
  | 'date'
  | 'file'
  | 'number'
  | 'link';
export type DefineQuestionWithOptionsKind = 'dropdown' | 'select' | 'multi_select';

@Injectable()
export class AskTemplatesFormService {
  private readonly formBuilder = inject(NonNullableFormBuilder);
  private readonly prosemirrorBasicConfig = inject(ProsemirrorBasicConfig);

  public createAskTemplateForm(template?: AskTemplate | AskPredefinedTemplate | null) {
    const symbolValue = template?.symbol?.type !== 'svg' ? template?.symbol : null;
    const templateValue = <
      Partial<AskTemplate & Omit<AskPredefinedTemplate, 'id' | 'autoAssignees'>>
    >(template ?? {});
    return this.formBuilder.group({
      name: this.formBuilder.control<string>(template?.name ?? '', [
        Validators.required,
        Validators.maxLength(255),
      ]),
      symbol: this.formBuilder.control<SymbolEmoji | null>(symbolValue ?? null),
      descriptionJson: this.formBuilder.control<object | null>(template?.descriptionJson ?? null),
      prioritySelectionEnabled: this.formBuilder.control<boolean>(
        templateValue.prioritySelectionEnabled ?? false,
      ),
      autoAssignments: this.formBuilder.control<string[]>(
        (templateValue.autoAssignees ?? []).map(assignee => assignee.guid),
        template ? [] : [Validators.required],
      ),
      active: this.formBuilder.control<boolean>(templateValue.active ?? false),
    });
  }

  public askTemplateFromDto<T extends AnyTemplateItem>(
    item: T,
  ): Extract<AskTemplateFormState, { kind: T['type'] }> {
    // TODO typescript cannot infer return type correctly here
    switch (item.type) {
      case 'string':
      case 'text':
      case 'boolean':
      case 'date':
      case 'file':
      case 'number':
      case 'link':
        return <Extract<AskTemplateFormState, { kind: T['type'] }>>this.questionFromDto(item);
      case 'dropdown':
      case 'select':
      case 'multi_select':
        return <Extract<AskTemplateFormState, { kind: T['type'] }>>(
          this.questionWithOptionsFromDto(item)
        );
      case 'infobox':
        return <Extract<AskTemplateFormState, { kind: T['type'] }>>{
          kind: item.type,
          hash: generateUuid(),
          value: {
            textJson: item.textJson,
          },
        };
      default:
        // This will show an error if an unsupported type is used
        const _typeGuard: never = item;
        throw new Error(`Unsupported template item type`);
    }
  }

  public askTemplateToDto<T extends AskTemplateFormState>(
    state: T,
  ): Extract<AnyTemplateItem, { type: T['kind'] }> {
    switch (state.kind) {
      case 'string':
      case 'text':
      case 'boolean':
      case 'date':
      case 'file':
      case 'number':
      case 'link':
        return <Extract<AnyTemplateItem, { type: T['kind'] }>>this.questionToDto(state);
      case 'dropdown':
      case 'select':
      case 'multi_select':
        return <Extract<AnyTemplateItem, { type: T['kind'] }>>this.questionWithOptionsToDto(state);
      case 'infobox':
        return <Extract<AnyTemplateItem, { type: T['kind'] }>>{
          type: state.kind,
          textJson: state.value.textJson,
        };
      default:
        // This will show an error if an unsupported type is used
        const _typeGuard: never = state;
        throw new Error(`Unsupported template item kind`);
    }
  }

  private questionFromDto<T extends Extract<AnyTemplateItem, { type: DefineQuestionKind }>>(
    item: T,
  ) {
    return <Extract<AskTemplateFormState, { kind: T['type'] }>>{
      kind: item.type,
      hash: item.hash,
      value: {
        question: item.name,
        hint: item.hint,
        required: item.required,
      },
    };
  }

  private questionWithOptionsFromDto<
    T extends Extract<AnyTemplateItem, { type: DefineQuestionWithOptionsKind }>,
  >(item: T) {
    return <Extract<AskTemplateFormState, { kind: T['type'] }>>{
      kind: item.type,
      hash: item.hash,
      value: {
        question: item.name,
        hint: item.hint,
        required: item.required,
        options: item.options.map(option => ({ value: option.value })),
      },
    };
  }

  private questionToDto<T extends Extract<AskTemplateFormState, { kind: DefineQuestionKind }>>(
    state: T,
  ) {
    return <Extract<AnyTemplateItem, { type: T['kind'] }>>{
      type: state.kind,
      name: state.value.question,
      hint: state.value.hint,
      required: state.value.required,
    };
  }

  private questionWithOptionsToDto<
    T extends Extract<AskTemplateFormState, { kind: DefineQuestionWithOptionsKind }>,
  >(state: T) {
    return <Extract<AnyTemplateItem, { type: T['kind'] }>>{
      type: state.kind,
      name: state.value.question,
      hint: state.value.hint,
      required: state.value.required,
      options: state.value.options.map(option => ({ value: option.value })),
    };
  }
}
